import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "../../clients/user_client";
import { AppContext } from "../../App";
import Image from "react-bootstrap/Image";
import { Form, Spinner } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import * as formik from "formik";
import * as yup from "yup";
import "./UserPageStyle.css";
import "../../components/Glass/GlassStyle.css";
import ChangePasswordModal from "../../components/User/ChangePasswordModal";
import { largeLogo } from "../../config_and_helpers/config";

// Yup schema for the login Formik form
const schema = yup.object({
  username: yup.string().required("Username is required"),
  password: yup.string().required("Password is required"),
});

/**
 * `LoginPage` is a React component that renders the login page.
 * This component captures username and password inputs, includes a "Remember Me" option, and has facilities for displaying error messages.
 * It is also able to handle form submission events, including validation checks and post-login navigation.
 *
 * Uses the following components:
 * - `AccountModal` - provides a sign up function.
 * - `ChangePasswordModal` - allows user to change their password.
 *
 * Uses the following hooks in its implementation:
 * - useState - to manage local states for login error visibility and form submission state.
 * - useNavigate - from `react-router-dom` which allows to programmatically navigate/react to changes in login state.
 * - useContext - to access the global context and to set user-related information upon successful login.
 *
 * Uses formik for form handling and yup for form validation.
 *
 * @returns A React Element displaying the Login page.
 */
const LoginPage = () => {
  const [loginError, setLoginError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nav = useNavigate();

  const {
    setToken,
    rememberMe,
    setRememberMe,
    setUserDetails,
    setIsSupplier,
    setNotifications,
  } = useContext(AppContext);

  const { Formik } = formik;

  return (
    <div className="glass-page glass-page--green login-page">
      <div className="login-page__inner">
        <Image src={largeLogo} className="login-page-logo" alt="Materiah" />

        <div className="glass-panel login-page__panel">
          <Formik
            initialValues={{ username: "", password: "" }}
            validationSchema={schema}
            onSubmit={(values) => {
              setIsSubmitting(true);
              login({
                credentials: values,
                setToken,
                setUserDetails,
                setIsSupplier,
                setNotifications,
                rememberMe,
              }).then((response) => {
                if (response && response.success) {
                  setLoginError(false);
                  nav("/");
                } else {
                  setLoginError(true);
                }
                setIsSubmitting(false);
              });
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              errors,
              touched,
            }) => (
              <Form
                noValidate
                onSubmit={handleSubmit}
                className="login-page__form"
              >
                <h1 className="login-page__heading">Welcome!</h1>

                <Form.Group
                  className="login-page__field position-relative"
                  controlId="loginUsername"
                >
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    size="lg"
                    type="text"
                    name="username"
                    value={values.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={
                      touched.username && !!errors.username && !values.username
                    }
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    className="position-absolute login-page__feedback"
                  >
                    {errors.username}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  className="login-page__field login-page__field--password position-relative"
                  controlId="loginPassword"
                  autoComplete="current-password"
                >
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    size="lg"
                    type="password"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={
                      touched.password && !!errors.password && !values.password
                    }
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    className="position-absolute login-page__feedback login-page__feedback--password"
                  >
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="login-page__remember" controlId="formRememberMe">
                  <Form.Check
                    type="checkbox"
                    label="Remember Me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                </Form.Group>

                {loginError && (
                  <div
                    className="alert alert-danger login-page__error mb-0"
                    role="alert"
                  >
                    Unable to log in with provided credentials
                  </div>
                )}

                {isSubmitting ? (
                  <Button
                    variant="primary"
                    className="glass-primary-btn login-page__submit"
                    disabled
                  >
                    <Spinner
                      size="sm"
                      as="span"
                      animation="border"
                      role="status"
                      aria-hidden="true"
                    />
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    className="glass-primary-btn login-page__submit"
                  >
                    Login
                  </Button>
                )}
              </Form>
            )}
          </Formik>

          <div className="login-page__footer">
            <ChangePasswordModal />
          </div>
        </div>
      </div>
    </div>
  );
};
export default LoginPage;
